import { proxy } from 'valtio';

import {
  clearSplitPaymentInFlight,
  isSplitPaymentInFlight,
  setSplitPaymentInFlight,
} from '@/lib/checkout/payments/splitPaymentSession';
import type { PaymentType, SplitPaymentConfig, PaymentRecord } from '@/lib/checkout/payments/types';

export type PaymentMethodConfiguration = {
  dropInConfiguration: Record<string, string | string[] | number | object | boolean> | null;
  name: string;
  type: PaymentType;
};

export type PaymentGatewayConfig = {
  id: string;
  paymentMethods: PaymentMethodConfiguration[];
};

export interface PaymentGatewaysState {
  amount: number | null;
  configs: PaymentGatewayConfig[];
  id: string | null;
  isSplitPayment: boolean;
  splitPaymentConfig: SplitPaymentConfig | undefined;
  partialAmount: number | null;
  paymentRecords: PaymentRecord[];
  selectedMethod: PaymentType | null;
  selectedGatewayId: string | null;
  /** Set by `useGetPaymentGateways` while `PaymentGatewayInitialize` is in flight. */
  isFetching: boolean;
}

export const paymentGatewaysState = proxy<PaymentGatewaysState>({
  configs: [],
  splitPaymentConfig: undefined,
  id: null,
  isSplitPayment: false,
  amount: null,
  partialAmount: null,
  paymentRecords: [],
  selectedMethod: null,
  selectedGatewayId: null,
  isFetching: false,
});

/**
 * True while an instalment plan is unfinished. `isSplitPayment` is memory-only
 * and already gone after a full-page 3DS return, so the persisted marker is
 * what keeps the partly-paid-order guards working on the way back.
 *
 * Example Input:
 * (split payment selected, page reloaded by a 3DS return)
 *
 * Example Output:
 * true
 */
export const hasSplitPaymentInFlight = (orderId?: string | null): boolean => {
  const entityId = orderId ?? paymentGatewaysState.id;
  return paymentGatewaysState.isSplitPayment || isSplitPaymentInFlight(entityId);
};

export const paymentGatewaysActions = {
  clear: (keepPartialAmount = false) => {
    paymentGatewaysState.id = null;
    paymentGatewaysState.amount = null;
    paymentGatewaysState.configs = [];
    paymentGatewaysState.isSplitPayment = false;
    paymentGatewaysState.selectedMethod = null;
    paymentGatewaysState.selectedGatewayId = null;
    if (!keepPartialAmount) {
      paymentGatewaysState.partialAmount = null;
      paymentGatewaysState.paymentRecords = [];
    }
    paymentGatewaysState.splitPaymentConfig = undefined;
    clearSplitPaymentInFlight();
  },

  set: (id: string, amount: number, configs: PaymentGatewayConfig[]) => {
    paymentGatewaysState.id = id;
    paymentGatewaysState.amount = amount;
    paymentGatewaysState.configs = configs;

    if (paymentGatewaysState.isSplitPayment) {
      setSplitPaymentInFlight(id);
    }
  },

  setSplitPaymentConfig: (config: SplitPaymentConfig) => {
    paymentGatewaysState.splitPaymentConfig = config;
    const hasSplitPayment =
      !!config?.maxPayments && !!config.paidMoney && config.paidMoney.amount > 0;
    if (hasSplitPayment) {
      paymentGatewaysState.isSplitPayment = true;
      paymentGatewaysState.partialAmount = config?.maxAmount ?? null;
    }
  },

  setIsSplitPayment: (enabled: boolean) => {
    paymentGatewaysState.isSplitPayment = enabled;

    if (enabled) {
      if (paymentGatewaysState.id) {
        setSplitPaymentInFlight(paymentGatewaysState.id);
      }
      return;
    }

    clearSplitPaymentInFlight();
  },

  setPartialAmount: (amount: number) => {
    paymentGatewaysState.partialAmount = amount;
  },

  selectMethod: (method: PaymentType | null, gatewayId: string | null) => {
    paymentGatewaysState.selectedMethod = method;
    paymentGatewaysState.selectedGatewayId = gatewayId;
  },

  addPaymentRecord: (record: PaymentRecord) => {
    paymentGatewaysState.paymentRecords.push(record);
  },

  updatePaymentRecord: (id: string, updates: Partial<PaymentRecord>) => {
    const index = paymentGatewaysState.paymentRecords.findIndex((r) => r.id === id);
    if (index !== -1) {
      paymentGatewaysState.paymentRecords[index] = {
        ...paymentGatewaysState.paymentRecords[index],
        ...updates,
      };
    }
  },

  clearPaymentRecords: () => {
    paymentGatewaysState.paymentRecords = [];
  },
};
